import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, BarChart3, CheckCircle2, GripVertical, Plus, Search, Tags, MapPin, Globe, Edit2, X, MoreVertical, UserCheck, Building2, ChevronDown } from 'lucide-react';
import supabase from '../../lib/supabase';
import { useOrgContext } from '../../context/OrgContext';

type LeadStage = 'new' | 'qualified' | 'scheduled' | 'won' | 'lost';
type Lead = {
  id: string;
  contact_id: string | null;
  title: string | null;
  stage: LeadStage;
  value_eur: number | null;
  created_at: string;
  updated_at?: string | null;
};
type Contact = {
  id: string;
  full_name: string | null;
  phone: string | null;
  email: string | null;
  source?: string | null;
  tags?: string[] | null;
  last_activity_at?: string | null;
  company?: string | null;
  company_address?: string | null;
  company_city?: string | null;
  company_zip?: string | null;
  google_maps_url?: string | null;
  website?: string | null;
};
type ContactOption = Contact;
type KPI = {
  contacts_total: number;
  leads_total: number;
  appts_scheduled: number;
  appts_completed: number;
  appts_no_show: number;
};

const stages: LeadStage[] = ['new', 'qualified', 'scheduled', 'won', 'lost'];
const openStages: LeadStage[] = ['new', 'qualified', 'scheduled'];

export const LeadsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { orgId, loading: orgLoading, currentUser } = useOrgContext();
  const canEdit = Boolean(currentUser?.canEdit);
  const navigate = useNavigate();
  const location = useLocation();

  const formatCurrency = useCallback(
    (value: number | null) => {
      if (value == null || Number.isNaN(value)) return t('crm.leads.na');
      const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'it-IT';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
    },
    [i18n.language, t]
  );

  const formatRelative = useCallback(
    (iso?: string | null) => {
      if (!iso) return '—';
      const diff = Date.now() - new Date(iso).getTime();
      const day = 1000 * 60 * 60 * 24;
      if (diff < day) return t('crm.common.relative.today');
      if (diff < day * 2) return t('crm.common.relative.yesterday');
      const days = Math.round(diff / day);
      return t('crm.common.relative.daysAgo', { count: days });
    },
    [t]
  );

  const stageLabels = useMemo(
    () => ({
      new: t('crm.leads.pipeline.headings.new'),
      qualified: t('crm.leads.pipeline.headings.qualified'),
      scheduled: t('crm.leads.pipeline.headings.scheduled'),
      won: t('crm.leads.pipeline.headings.won'),
      lost: t('crm.leads.pipeline.headings.lost'),
    }),
    [t]
  );

  // Color scheme for Leads (Money/Sales theme)
  const getStageColors = (stage: LeadStage) => {
    switch (stage) {
      case 'qualified':
        return {
          border: 'border-yellow-400 dark:border-yellow-500',
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          text: 'text-yellow-700 dark:text-yellow-300',
          headerBg: 'bg-yellow-100 dark:bg-yellow-900/30',
        };
      case 'won':
        return {
          border: 'border-emerald-500 dark:border-emerald-400',
          bg: 'bg-emerald-50 dark:bg-emerald-900/20',
          text: 'text-emerald-700 dark:text-emerald-300',
          headerBg: 'bg-emerald-100 dark:bg-emerald-900/30',
        };
      case 'lost':
        return {
          border: 'border-red-300 dark:border-red-600',
          bg: 'bg-red-50 dark:bg-red-900/20',
          text: 'text-red-700 dark:text-red-300',
          headerBg: 'bg-red-100 dark:bg-red-900/30',
        };
      case 'scheduled':
        return {
          border: 'border-blue-300 dark:border-blue-600',
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          text: 'text-blue-700 dark:text-blue-300',
          headerBg: 'bg-blue-100 dark:bg-blue-900/30',
        };
      default: // 'new'
        return {
          border: 'border-gray-200 dark:border-gray-700',
          bg: 'bg-gray-50 dark:bg-gray-700/50',
          text: 'text-gray-900 dark:text-gray-100',
          headerBg: 'bg-gray-100 dark:bg-gray-700/50',
        };
    }
  };

  const [leads, setLeads] = useState<Lead[]>([]);
  const [contacts, setContacts] = useState<Record<string, Contact>>({});
  const [modalMode, setModalMode] = useState<'new' | 'edit'>('new');
  const [showModal, setShowModal] = useState(false);
  const [activeLeadId, setActiveLeadId] = useState<string | null>(null);
  const [leadForm, setLeadForm] = useState({ title: '', value: '', stage: 'new' as LeadStage, contactName: '', contactPhone: '', contactEmail: '', notes: '' });
  const [confirmDelete, setConfirmDelete] = useState<Lead | null>(null);
  const [contactSearch, setContactSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [contactOptions, setContactOptions] = useState<ContactOption[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactOption | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<ContactOption[]>([]);
  const [contactWarning, setContactWarning] = useState<string | null>(null);
  const [showContactList, setShowContactList] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [kpi, setKpi] = useState<KPI | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<LeadStage | null>(null);
  const [openMenuLeadId, setOpenMenuLeadId] = useState<string | null>(null);
  const [selectedStageFilter, setSelectedStageFilter] = useState<LeadStage | 'all'>('all'); // Mobile filter chip

  const refreshLeads = useCallback(async () => {
    if (!orgId) {
      setLeads([]);
      return;
    }
    console.log('[Leads] Fetching leads for orgId:', orgId);
    
    // Try with notes first, fallback without notes if column doesn't exist
    let resp = await supabase
      .from('leads')
      .select('id, contact_id, title, stage, value_eur, created_at, notes, org_id')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(200);
    
    // If error and it's about missing column, try without notes
    if (resp.error) {
      const errorMessage = resp.error.message || '';
      const errorCode = resp.error.code || '';
      const isColumnError = errorMessage.includes('notes') || 
                           errorMessage.includes('column') || 
                           errorMessage.includes('Could not find') ||
                           errorCode === 'PGRST116' ||
                           errorCode === '42703';
      
      if (isColumnError) {
        // Silently retry without notes column - this is expected if column doesn't exist
        const fallbackResp = await supabase
          .from('leads')
          .select('id, contact_id, title, stage, value_eur, created_at, org_id')
          .eq('org_id', orgId)
          .order('created_at', { ascending: false })
          .limit(200);
        if (!fallbackResp.error && fallbackResp.data) {
          resp = { ...fallbackResp, data: fallbackResp.data.map((lead: any) => ({ ...lead, notes: null })) };
        } else {
          resp = fallbackResp;
        }
      }
    }
    
    console.log('[Leads] Query response:', { 
      error: resp.error, 
      dataCount: resp.data?.length, 
      hasData: !!resp.data 
    });
    
    if (resp.error) {
      console.error('[Leads] Error fetching leads:', resp.error);
      setLeads([]);
      return;
    }
    
    if (resp.data) {
      const normalized = (resp.data as any[]).map((row) => {
        // Debug: log raw stage value
        console.log('[Leads] Raw lead data:', { id: row.id, stage: row.stage, stageType: typeof row.stage, title: row.title });
        
        // Ensure stage is valid - check for null, undefined, or invalid values
        let validStage: LeadStage = 'new';
        if (row.stage && typeof row.stage === 'string' && stages.includes(row.stage as LeadStage)) {
          validStage = row.stage as LeadStage;
        } else {
          console.warn('[Leads] Invalid stage for lead:', row.id, 'stage:', row.stage, 'defaulting to "new"');
        }
        
        return {
          ...row,
          stage: validStage,
          notes: row.notes || null, // Notes might not exist, default to null
          updated_at: row.updated_at ?? null,
        } as Lead;
      });
      console.log('[Leads] Fetched leads:', normalized.length, 'leads with stages:', normalized.map(l => ({ id: l.id, stage: l.stage, title: l.title })));
      setLeads(normalized);
    } else {
      setLeads([]);
    }
  }, [orgId]);

  const refreshContacts = useCallback(async () => {
    if (!orgId) return;
    // Try with website first, fallback without website if column doesn't exist
    let resp = await supabase
      .from('contacts')
      .select('id, full_name, phone, email, source, tags, last_activity_at, company, company_address, company_city, company_zip, google_maps_url, website')
      .eq('org_id', orgId)
      .limit(500);
    
    // If error and it's about missing column, try without website
    if (resp.error) {
      const errorMessage = resp.error.message || '';
      if (errorMessage.includes('website') || errorMessage.includes('column') || resp.error.code === 'PGRST116') {
        console.warn('[Leads] Website column not found in contacts, retrying without website field');
        const fallbackResp = await supabase
          .from('contacts')
          .select('id, full_name, phone, email, source, tags, last_activity_at, company, company_address, company_city, company_zip, google_maps_url')
          .eq('org_id', orgId)
          .limit(500);
        if (!fallbackResp.error && fallbackResp.data) {
          resp = { ...fallbackResp, data: fallbackResp.data.map((contact: any) => ({ ...contact, website: null })) };
        } else {
          resp = fallbackResp;
        }
      }
    }
    
    if (!resp.error && resp.data) {
      const map: Record<string, Contact> = {};
      (resp.data as Contact[]).forEach((c) => {
        if (c.id) {
          map[c.id] = {
            ...c,
            website: c.website || null, // Ensure website is set to null if it doesn't exist
          };
        }
      });
      setContacts(map);
    }
  }, [orgId]);

  const refreshKpi = useCallback(async () => {
    if (!orgId) return;
    const { data, error } = await supabase
      .from('crm_kpi')
      .select('contacts_total, leads_total, appts_scheduled, appts_completed, appts_no_show')
      .eq('org_id', orgId)
      .maybeSingle();
    if (!error && data) setKpi(data as KPI);
  }, [orgId]);

  useEffect(() => {
    if (!orgId) {
      if (!orgLoading) {
        setLeads([]);
        setContacts({});
      }
      return;
    }

    let active = true;
    (async () => {
      // First, fix any leads with null or invalid stages
      // Get all leads for this org
      const { data: allLeads } = await supabase
        .from('leads')
        .select('id, stage')
        .eq('org_id', orgId);
      
      if (allLeads) {
        // Find leads with null or invalid stages
        const leadsToFix = allLeads.filter(lead => {
          const stage = lead.stage;
          return !stage || !stages.includes(stage as LeadStage);
        });
        
        if (leadsToFix.length > 0) {
          console.log('[Leads] Found', leadsToFix.length, 'leads with invalid stages, fixing...');
          const leadIds = leadsToFix.map(l => l.id);
          const { error: updateError } = await supabase
            .from('leads')
            .update({ stage: 'new' })
            .in('id', leadIds)
            .eq('org_id', orgId);
          
          if (updateError) {
            console.error('[Leads] Error fixing leads:', updateError);
          } else {
            console.log('[Leads] Fixed', leadIds.length, 'leads by setting stage to "new"');
          }
        }
      }
      
      await Promise.all([refreshLeads(), refreshContacts(), refreshKpi()]);
      if (!active) return;
    })();

    return () => {
      active = false;
    };
  }, [orgId, orgLoading, refreshLeads, refreshContacts, refreshKpi, location.pathname]);

  // Extract unique first tags (business categories) from contacts
  const tags = useMemo(() => {
    const unique = new Set<string>();
    Object.values(contacts).forEach((contact) => {
      const firstTag = (contact.tags || [])[0];
      if (firstTag) {
        unique.add(firstTag);
      }
    });
    return Array.from(unique).sort();
  }, [contacts]);

  const grouped = useMemo(() => {
    const map: Record<LeadStage, Lead[]> = { new: [], qualified: [], scheduled: [], won: [], lost: [] };
    console.log('[Leads] Grouping', leads.length, 'leads. Lead stages:', leads.map(l => ({ id: l.id, stage: l.stage, stageType: typeof l.stage })));
    
    const searchLower = searchTerm.trim().toLowerCase();
    
    leads.forEach((lead) => {
      // Filter by search term if provided
      if (searchLower) {
        const contact = lead.contact_id ? contacts[lead.contact_id] : undefined;
        const titleMatch = lead.title?.toLowerCase().includes(searchLower) ?? false;
        const contactNameMatch = contact?.full_name?.toLowerCase().includes(searchLower) ?? false;
        if (!titleMatch && !contactNameMatch) {
          return; // Skip this lead if it doesn't match the search
        }
      }
      
      // Filter by tags if selected
      if (selectedTags.length > 0) {
        const contact = lead.contact_id ? contacts[lead.contact_id] : undefined;
        if (!contact) return; // Skip if no contact
        const firstTag = (contact.tags || [])[0];
        if (!firstTag) return; // Skip if no tags
        const tagMatches = selectedTags.some(selectedTag => 
          firstTag.toLowerCase() === selectedTag.toLowerCase()
        );
        if (!tagMatches) return; // Skip if tag doesn't match
      }
      
      // Ensure stage is valid, default to 'new' if invalid
      let stage: LeadStage = 'new';
      if (lead.stage && typeof lead.stage === 'string' && stages.includes(lead.stage as LeadStage)) {
        stage = lead.stage as LeadStage;
      } else {
        console.warn('[Leads] Invalid stage in lead object:', lead.id, 'stage:', lead.stage, 'type:', typeof lead.stage);
      }
      
      if (map[stage]) {
        map[stage] = [...map[stage], lead];
      } else {
        // If stage is somehow not in the map, add to 'new'
        console.warn('[Leads] Stage not in map, adding to new:', stage, 'lead:', lead.id);
        map.new = [...map.new, lead];
      }
    });
    
    const groupedSummary = Object.keys(map).reduce((acc, key) => {
      acc[key] = map[key as LeadStage].length;
      return acc;
    }, {} as Record<string, number>);
    console.log('[Leads] Grouped leads summary:', groupedSummary);
    console.log('[Leads] Full grouped map:', map);
    return map;
  }, [leads, contacts, searchTerm, selectedTags]);

  const stageTotals = useMemo(
    () => stages.reduce((acc, stage) => ({ ...acc, [stage]: grouped[stage].length }), {} as Record<LeadStage, number>),
    [grouped]
  );

  const conversionRate = useMemo(() => {
    if (leads.length === 0) return 0;
    return Math.round((grouped.won.length / leads.length) * 100);
  }, [grouped, leads.length]);

  const staleFollowUps = useMemo(() => {
    const threeDays = 1000 * 60 * 60 * 24 * 3;
    return leads
      .filter((lead) =>
        openStages.includes(lead.stage) && Date.now() - new Date(lead.updated_at ?? lead.created_at).getTime() > threeDays
      )
      .slice(0, 4);
  }, [leads]);

  const warningDuplicates = useMemo(() => {
    const seen = new Map<string, number>();
    grouped.new.concat(grouped.qualified, grouped.scheduled).forEach((lead) => {
      if (lead.contact_id) {
        seen.set(lead.contact_id, (seen.get(lead.contact_id) || 0) + 1);
      }
    });
    return new Set(Array.from(seen.entries()).filter(([, count]) => count > 1).map(([contactId]) => contactId));
  }, [grouped]);

  async function moveLead(id: string, stage: LeadStage) {
    if (!canEdit) return;
    if (!orgId) return;
    const { error } = await supabase.from('leads').update({ stage }).eq('id', id).eq('org_id', orgId);
    if (!error) {
      setLeads((prev) => prev.map((lead) => (lead.id === id ? { ...lead, stage } : lead)));
      await refreshKpi();
    }
  }

  const handleDragStart = (leadId: string) => {
    if (!canEdit) return;
    setDraggingId(leadId);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverStage(null);
  };

  const handleDragOver = (stage: LeadStage, event: React.DragEvent<HTMLDivElement>) => {
    if (!canEdit || !draggingId) return;
    event.preventDefault();
    if (dragOverStage !== stage) setDragOverStage(stage);
  };

  const handleDragLeave = (stage: LeadStage) => {
    if (dragOverStage === stage) {
      setDragOverStage(null);
    }
  };

  const handleDrop = async (stage: LeadStage) => {
    if (!canEdit || !draggingId) return;
    const lead = leads.find((item) => item.id === draggingId);
    setDragOverStage(null);
    setDraggingId(null);
    if (!lead || lead.stage === stage) return;
    await moveLead(draggingId, stage);
  };

  const handleConvertLeadToProspect = useCallback(async (lead: Lead) => {
    if (!canEdit || !orgId || !lead.contact_id) {
      alert('Impossibile convertire: contatto non associato al lead.');
      return;
    }
    
    const { error } = await supabase
      .from('contacts')
      .update({
        prospect_status: 'to_visit',
        lifecycle_stage: 'contact',
      })
      .eq('id', lead.contact_id)
      .eq('org_id', orgId);
    
    if (error) {
      alert(`Errore: ${error.message}`);
      return;
    }
    
    // Optionally delete the lead since it's now a prospect
    const shouldDeleteLead = confirm('Lead convertito in prospect. Vuoi eliminare il lead?');
    if (shouldDeleteLead) {
      const { error: deleteError } = await supabase
        .from('leads')
        .delete()
        .eq('id', lead.id)
        .eq('org_id', orgId);
      if (deleteError) {
        alert(`Errore eliminazione lead: ${deleteError.message}`);
      }
    }
    
    await refreshLeads();
    setOpenMenuLeadId(null);
  }, [canEdit, orgId, refreshLeads]);

  const handleConvertLeadToClient = useCallback(async (lead: Lead) => {
    if (!canEdit || !orgId || !lead.contact_id) {
      alert('Impossibile convertire: contatto non associato al lead.');
      return;
    }
    
    const { error } = await supabase
      .from('contacts')
      .update({
        lifecycle_stage: 'client',
        prospect_status: null,
      })
      .eq('id', lead.contact_id)
      .eq('org_id', orgId);
    
    if (error) {
      alert(`Errore: ${error.message}`);
      return;
    }
    
    // Optionally delete the lead since it's now a client
    const shouldDeleteLead = confirm('Lead convertito in cliente. Vuoi eliminare il lead?');
    if (shouldDeleteLead) {
      const { error: deleteError } = await supabase
        .from('leads')
        .delete()
        .eq('id', lead.id)
        .eq('org_id', orgId);
      if (deleteError) {
        alert(`Errore eliminazione lead: ${deleteError.message}`);
      }
    }
    
    await refreshLeads();
    setOpenMenuLeadId(null);
  }, [canEdit, orgId, refreshLeads]);

  function openNewModal() {
    if (!canEdit) return;
    setModalMode('new');
    setActiveLeadId(null);
    setLeadForm({ title: '', value: '', stage: 'new', contactName: '', contactPhone: '', contactEmail: '', notes: '' });
    setContactSearch('');
    setContactOptions([]);
    setSelectedContact(null);
    setSelectedContacts([]);
    setContactWarning(null);
    setMultiSelectMode(false);
    setShowModal(true);
  }

  function openEditModal(lead: Lead) {
    if (!canEdit) return;
    setModalMode('edit');
    setActiveLeadId(lead.id);
    const contact = lead.contact_id ? contacts[lead.contact_id] : undefined;
    const option = contact ? { ...contact } : null;
    setLeadForm({
      title: lead.title || '',
      value: lead.value_eur != null ? String(lead.value_eur) : '',
      stage: lead.stage,
      contactName: contact?.full_name || '',
      contactPhone: contact?.phone || '',
      contactEmail: contact?.email || '',
      notes: (lead as any).notes || '',
    });
    setContactSearch(option?.full_name || option?.email || '');
    setContactOptions([]);
    setSelectedContact(option);
    setContactWarning(null);
    setShowModal(true);
  }

  async function fetchContactOptions(term: string) {
    if (!orgId) return;
    setContactLoading(true);
    const query = supabase
      .from('contacts')
      .select('id, full_name, email, phone, last_activity_at, source, tags')
      .eq('org_id', orgId)
      .limit(10);
    if (term.trim()) {
      query.or(`full_name.ilike.%${term.trim()}%,email.ilike.%${term.trim()}%,phone.ilike.%${term.trim()}%`);
    }
    const { data, error } = await query;
    if (!error && data) setContactOptions(data as ContactOption[]);
    setContactLoading(false);
  }

  useEffect(() => {
    if (!showModal || modalMode !== 'new') return;
    const timeout = setTimeout(() => {
      if (!selectedContact) fetchContactOptions(contactSearch);
    }, 250);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactSearch, showModal, modalMode, selectedContact?.id]);

  useEffect(() => {
    if (!selectedContact?.id || !orgId) {
      setContactWarning(null);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from('leads')
        .select('id')
        .eq('org_id', orgId)
        .eq('contact_id', selectedContact.id)
        .in('stage', openStages);
      if (!error && data && data.length > 0) {
        setContactWarning(`Il contatto ha già ${data.length} lead aperti.`);
      } else {
        setContactWarning(null);
      }
    })();
  }, [selectedContact, orgId]);

  async function handleSaveLead() {
    if (!canEdit) return;
    if (!orgId) return;
    if (modalMode === 'new') {
      // Multi-select mode: create leads for multiple contacts
      if (multiSelectMode && selectedContacts.length > 0) {
        const leadsToCreate = selectedContacts.map(contact => ({
          title: leadForm.title || `Lead: ${contact.full_name || contact.email || 'Nuovo'}`,
          value_eur: leadForm.value ? Number(leadForm.value) : null,
          contact_id: contact.id,
          stage: leadForm.stage,
          org_id: orgId,
          notes: leadForm.notes || null,
        }));

        const { error: leadError } = await supabase
          .from('leads')
          .insert(leadsToCreate);

        if (leadError) {
          alert(`Errore creazione lead: ${leadError.message}`);
          return;
        }

        setShowModal(false);
        setSelectedContacts([]);
        setMultiSelectMode(false);
        await refreshLeads();
        await refreshKpi();
        return;
      }

      // Single contact mode
      let contactId: string | null = selectedContact?.id ?? null;

      if (!contactId && (leadForm.contactName || leadForm.contactPhone || leadForm.contactEmail)) {
        const { data, error } = await supabase
          .from('contacts')
          .insert([
            {
              full_name: leadForm.contactName || null,
              phone: leadForm.contactPhone || null,
              email: leadForm.contactEmail || null,
              org_id: orgId,
              source: 'manual',
            },
          ])
          .select('id')
          .single();
        if (error) {
          alert(error.message);
          return;
        }
        contactId = data?.id ?? null;
      }

      const payload: any = {
        title: leadForm.title || 'New Lead',
        value_eur: leadForm.value ? Number(leadForm.value) : null,
        contact_id: contactId,
        stage: leadForm.stage,
        org_id: orgId,
      };
      
      // Try to add notes if provided (only if column exists - handled by backend)
      if (leadForm.notes && leadForm.notes.trim()) {
        payload.notes = leadForm.notes;
      }

      let { error: leadError } = await supabase
        .from('leads')
        .insert([payload]);

      // If error is about notes column, retry without notes
      if (leadError && (leadError.message?.includes('notes') || leadError.message?.includes('column') || leadError.code === 'PGRST116')) {
        const { notes, ...payloadWithoutNotes } = payload;
        const retryResult = await supabase
          .from('leads')
          .insert([payloadWithoutNotes]);
        
        if (retryResult.error) {
          alert(retryResult.error.message);
          return;
        }
      } else if (leadError) {
        alert(leadError.message);
        return;
      }

      if (contactId && !selectedContact) {
        const { data: newContactRows } = await supabase
          .from('contacts')
          .select('id, full_name, email, phone, source, tags, last_activity_at')
          .eq('id', contactId)
          .maybeSingle();
        if (newContactRows) {
          setContacts((prev) => ({ ...prev, [contactId as string]: newContactRows as Contact }));
        }
      }
    } else if (activeLeadId) {
      const updatePayload: any = {
        title: leadForm.title || 'Untitled Lead',
        value_eur: leadForm.value ? Number(leadForm.value) : null,
        stage: leadForm.stage,
      };
      
      // Try to add notes if provided (only if column exists - handled by backend)
      // We'll let the update fail gracefully if notes column doesn't exist
      if (leadForm.notes !== undefined && leadForm.notes !== '') {
        updatePayload.notes = leadForm.notes;
      }

      const { error } = await supabase
        .from('leads')
        .update(updatePayload)
        .eq('id', activeLeadId)
        .eq('org_id', orgId);

      if (error) {
        // If error is about notes column, retry without notes
        if (error.message?.includes('notes') || error.message?.includes('column') || error.code === 'PGRST116') {
          const { notes, ...updateWithoutNotes } = updatePayload;
          const { error: retryError } = await supabase
            .from('leads')
            .update(updateWithoutNotes)
            .eq('id', activeLeadId)
            .eq('org_id', orgId);
          
          if (retryError) {
            alert(retryError.message);
            return;
          }
        } else {
          alert(error.message);
          return;
        }
      }
    }

    setShowModal(false);
    setSelectedContact(null);
    setShowContactList(false);
    await refreshLeads();
    await refreshKpi();
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuLeadId && !(event.target as HTMLElement).closest('.relative')) {
        setOpenMenuLeadId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuLeadId]);

  // Close tags dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tagsDropdownOpen && !(event.target as HTMLElement).closest('.relative')) {
        setTagsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tagsDropdownOpen]);

  async function handleDeleteLead(id: string) {
    if (!canEdit) return;
    if (!orgId) return;
    const { error } = await supabase.from('leads').delete().eq('id', id).eq('org_id', orgId);
    if (error) {
      alert(error.message);
      return;
    }
    setConfirmDelete(null);
    await refreshLeads();
    await refreshKpi();
  }

  return (
    <div className="space-y-4 lg:space-y-6 w-full max-w-full overflow-x-hidden">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('crm.leads.title')}</h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{t('crm.leads.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Search Bar */}
          <div className="relative flex-1 sm:flex-initial sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Cerca lead..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Tag Filter Dropdown */}
          <div className="relative flex-1 sm:flex-initial sm:min-w-[200px]">
            <button
              type="button"
              onClick={() => setTagsDropdownOpen(!tagsDropdownOpen)}
              className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-left flex items-center justify-between min-h-[38px]"
            >
              <span className="truncate">
                {selectedTags.length === 0
                  ? t('crm.common.filterTag')
                  : selectedTags.length === 1
                  ? selectedTags[0]
                  : t('crm.common.selected', { count: selectedTags.length })}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${tagsDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {tagsDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full min-w-[250px] max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
                {tags.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">Nessun tag disponibile</div>
                ) : (
                  <>
                    {tags.map((tag) => {
                      const isSelected = selectedTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedTags(selectedTags.filter(t => t !== tag));
                            } else {
                              setSelectedTags([...selectedTags, tag]);
                            }
                          }}
                          className={`w-full text-left px-3 py-2 text-xs sm:text-sm transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-center gap-2 ${
                            isSelected
                              ? 'bg-cyan-50 dark:bg-cyan-900/30 hover:bg-cyan-100 dark:hover:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="flex-1">{tag}</span>
                        </button>
                      );
                    })}
                    {selectedTags.length > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTags([]);
                            setTagsDropdownOpen(false);
                          }}
                          className="w-full px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          Cancella tutti
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          {canEdit && (
            <button
              onClick={openNewModal}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors text-xs sm:text-sm shadow-md"
            >
              <Plus className="w-4 h-4" /> {t('crm.leads.newLead')}
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('crm.leads.kpi.totalLeads')}</div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">{leads.length}</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Distribuiti: {stageTotals.new} Nuovi · {stageTotals.qualified} Qualificati · {stageTotals.scheduled} Schedulati · {stageTotals.won} Vinti · {stageTotals.lost} Persi
          </div>
          {leads.length !== (kpi?.leads_total ?? leads.length) && (
            <div className="mt-1 text-xs text-orange-600 dark:text-orange-400">
              (KPI mostra: {kpi?.leads_total ?? 'N/A'}, Fetched: {leads.length})
            </div>
          )}
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('crm.leads.kpi.conversion')}</div>
          <div className="mt-2 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">{conversionRate}%</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('crm.leads.kpi.conversionDetail', { won: grouped.won.length, total: leads.length || 1 })}</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('crm.leads.kpi.scheduled')}</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            <BarChart3 className="w-5 h-5 text-sky-600 dark:text-sky-400" />
            {kpi?.appts_scheduled ?? '—'}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('crm.leads.kpiSummary', { completed: kpi?.appts_completed ?? '—', noShow: kpi?.appts_no_show ?? '—' })}</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('crm.leads.followUps.title')}</div>
          {staleFollowUps.length === 0 ? (
            <div className="mt-3 text-sm text-emerald-600 dark:text-emerald-400">{t('crm.leads.followUps.empty')}</div>
          ) : (
            <ul className="mt-2 space-y-2 text-xs text-gray-700 dark:text-gray-300">
              {staleFollowUps.map((lead) => (
                <li key={lead.id} className="flex items-center justify-between">
                  <span className="truncate">{lead.title || t('crm.leads.defaults.fallbackTitle')}</span>
                  <span className="text-gray-500 dark:text-gray-400">{formatRelative(lead.updated_at || lead.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Mobile: Filter Chip Row */}
      <div className="lg:hidden mb-4 px-4">
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <button
            onClick={() => setSelectedStageFilter('all')}
            className={`flex-shrink-0 px-4 py-2 rounded-full font-semibold text-sm min-h-[44px] transition-all ${
              selectedStageFilter === 'all'
                ? 'bg-cyan-500 dark:bg-cyan-600 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            Tutti ({leads.length})
          </button>
          {stages.map((stage) => (
            <button
              key={stage}
              onClick={() => setSelectedStageFilter(stage)}
              className={`flex-shrink-0 px-4 py-2 rounded-full font-semibold text-sm min-h-[44px] transition-all ${
                selectedStageFilter === stage
                  ? 'bg-cyan-500 dark:bg-cyan-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {stageLabels[stage]} ({stageTotals[stage]})
            </button>
          ))}
        </div>
      </div>

      {/* Desktop: Kanban Columns (Hidden on Mobile) */}
      <div 
        className="hidden lg:block overflow-x-auto -mx-4 lg:mx-0 pb-4 lg:pb-0" 
        style={{ 
          WebkitOverflowScrolling: 'touch', 
          scrollbarWidth: 'thin',
          msOverflowStyle: '-ms-autohiding-scrollbar'
        }}
      >
        <div className="grid lg:grid-cols-5 gap-4 px-4 lg:px-0">
          {stages.map((stage) => {
            const colors = getStageColors(stage);
            return (
            <div
              key={stage}
              className={`border rounded-lg p-2.5 sm:p-3 flex flex-col min-h-[280px] sm:min-h-[320px] w-[280px] sm:w-auto lg:w-auto transition-colors snap-start lg:snap-none ${
                dragOverStage === stage 
                  ? 'border-cyan-500 dark:border-cyan-400 bg-cyan-50 dark:bg-cyan-900/30' 
                  : `${colors.border} ${colors.bg}`
              }`}
            onDragOver={(event) => handleDragOver(stage, event)}
            onDrop={() => handleDrop(stage)}
            onDragLeave={() => handleDragLeave(stage)}
          >
            <div className={`font-semibold mb-2 capitalize flex items-center gap-2 text-sm px-2 py-1.5 rounded ${colors.headerBg} ${colors.text}`}>
              <GripVertical className="w-4 h-4 flex-shrink-0 opacity-70" />
              <span className="truncate">{stageLabels[stage]}</span>
              <span className="text-xs opacity-80 flex-shrink-0">{stageTotals[stage]}</span>
            </div>
            <div className="flex flex-col gap-2 min-h-[200px]">
              {grouped[stage].map((lead) => {
                const contact = lead.contact_id ? contacts[lead.contact_id] : undefined;
                const hasDuplicate = lead.contact_id ? warningDuplicates.has(lead.contact_id) : false;
                const source = contact?.source;
                return (
                  <div
                    key={lead.id}
                    className={`p-2.5 sm:p-3 rounded-lg border bg-white dark:bg-gray-800 space-y-1.5 sm:space-y-2 transition-opacity shadow-sm ${
                      draggingId === lead.id ? 'opacity-70 border-cyan-500 dark:border-cyan-400' : 'border-gray-200 dark:border-gray-700'
                    }`}
                    draggable={canEdit}
                    onDragStart={() => handleDragStart(lead.id)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-xs sm:text-sm line-clamp-2">
                      {contact ? (contact.company || contact.full_name || contact.email || lead.title || 'Untitled Lead') : (lead.title || 'Untitled Lead')}
                    </div>
                    {contact && contact.full_name && contact.company && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {contact.full_name}
                      </div>
                    )}
                    {lead.value_eur != null && (
                      <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(lead.value_eur)}
                      </div>
                    )}
                    {contact?.company_address && (
                      <div className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span className="line-clamp-1">{contact.company_address}{contact.company_city && `, ${contact.company_city}`}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {contact?.google_maps_url && (
                        <a
                          href={contact.google_maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 flex items-center gap-1"
                        >
                          <MapPin className="w-3 h-3" />
                          Maps
                        </a>
                      )}
                      {contact?.website && (
                        <a
                          href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 flex items-center gap-1"
                        >
                          <Globe className="w-3 h-3" />
                          Website
                        </a>
                      )}
                    </div>
                    {contact?.tags && contact.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {contact.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="inline-flex items-center rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-1.5 py-0.5 text-[10px] text-gray-700 dark:text-gray-300">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {contact?.phone && (
                      <div className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                        <span>📞</span> {contact.phone}
                      </div>
                    )}
                    {contact?.email && (
                      <div className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                        <span>✉️</span> {contact.email}
                      </div>
                    )}
                    {source && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {source}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {formatRelative(lead.updated_at || lead.created_at)}
                    </div>
                    {hasDuplicate && (
                      <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="w-3 h-3" /> Contatto duplicato nel funnel
                      </div>
                    )}
                    {canEdit && (
                      <div className="flex flex-col gap-1.5 mt-2">
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => openEditModal(lead)}
                            className="flex-1 px-3 py-1.5 rounded-lg border border-cyan-500 dark:border-cyan-400 text-cyan-600 dark:text-cyan-400 font-semibold hover:bg-cyan-50 dark:hover:bg-cyan-900/30 transition-colors text-xs flex items-center justify-center gap-1.5"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                            {t('crm.common.buttons.edit')}
                          </button>
                          <button
                            onClick={() => navigate(`/crm/leads/${lead.id}`)}
                            className="flex-1 px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            {t('crm.leads.modal.details')}
                          </button>
                          {/* Actions Menu */}
                          {lead.contact_id && (
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setOpenMenuLeadId(openMenuLeadId === lead.id ? null : lead.id);
                                }}
                                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-xs flex items-center justify-center"
                                title="Azioni"
                              >
                                <MoreVertical className="w-3.5 h-3.5" />
                              </button>
                              {openMenuLeadId === lead.id && (
                                <div className="absolute right-0 top-full mt-1 z-[100] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg min-w-[180px]">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleConvertLeadToProspect(lead);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                                  >
                                    <UserCheck className="w-4 h-4" />
                                    Converti in Prospect
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleConvertLeadToClient(lead);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                                  >
                                    <Building2 className="w-4 h-4" />
                                    Converti in Cliente
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {grouped[stage].length === 0 && (
                <div className="p-3 border border-dashed border-gray-300 dark:border-gray-600 rounded text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 text-center">
                  Nessun lead
                </div>
              )}
            </div>
          </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: Vertical List (Only on Mobile) */}
      <div className="lg:hidden space-y-3 pb-20">
        {(selectedStageFilter === 'all' 
          ? leads.filter(lead => {
              const searchLower = searchTerm.trim().toLowerCase();
              if (searchLower) {
                const contact = lead.contact_id ? contacts[lead.contact_id] : undefined;
                const titleMatch = lead.title?.toLowerCase().includes(searchLower) ?? false;
                const contactNameMatch = contact?.full_name?.toLowerCase().includes(searchLower) ?? false;
                if (!titleMatch && !contactNameMatch) return false;
              }
              if (selectedTags.length > 0) {
                const contact = lead.contact_id ? contacts[lead.contact_id] : undefined;
                if (!contact) return false;
                const firstTag = (contact.tags || [])[0];
                if (!firstTag) return false;
                const tagMatches = selectedTags.some(selectedTag => 
                  firstTag.toLowerCase() === selectedTag.toLowerCase()
                );
                if (!tagMatches) return false;
              }
              return true;
            })
          : grouped[selectedStageFilter]
        ).map((lead) => {
          const contact = lead.contact_id ? contacts[lead.contact_id] : undefined;
          
          // Get border color from stage
          let borderColor = '#e5e7eb'; // gray default
          if (lead.stage === 'qualified') borderColor = '#facc15'; // yellow
          else if (lead.stage === 'won') borderColor = '#10b981'; // green
          else if (lead.stage === 'lost') borderColor = '#ef4444'; // red
          else if (lead.stage === 'scheduled') borderColor = '#60a5fa'; // blue
          
          return (
            <div
              key={lead.id}
              className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm active:scale-[0.98] transition-transform"
              style={{ borderLeftColor: borderColor, borderLeftWidth: '4px' }}
            >
              {/* Company/Contact Name - Large, Bold */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 truncate">
                {contact ? (contact.company || contact.full_name || contact.email || lead.title || 'Untitled Lead') : (lead.title || 'Untitled Lead')}
              </h3>
              
              {/* Secondary Info */}
              {contact && contact.full_name && contact.company && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {contact.full_name}
                </p>
              )}
              
              {/* Value */}
              {lead.value_eur != null && (
                <div className="text-base font-bold text-emerald-600 dark:text-emerald-400 mb-2">
                  {formatCurrency(lead.value_eur)}
                </div>
              )}
              
              {/* Phone */}
              {contact?.phone && (
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 mb-2">
                  <span className="text-lg">📞</span>
                  <span className="text-sm">{contact.phone}</span>
                </div>
              )}
              
              {/* Address */}
              {contact?.company_address && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 mb-2">
                  <MapPin className="w-4 h-4" />
                  <span className="truncate">{contact.company_address}{contact.company_city && `, ${contact.company_city}`}</span>
                </div>
              )}
              
              {/* Actions */}
              {canEdit && (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => openEditModal(lead)}
                    className="flex-1 px-4 py-3 rounded-lg border-2 border-cyan-500 dark:border-cyan-400 text-cyan-600 dark:text-cyan-400 font-semibold hover:bg-cyan-50 dark:hover:bg-cyan-900/30 transition-colors text-sm min-h-[44px]"
                  >
                    Modifica
                  </button>
                  <button
                    onClick={() => navigate(`/crm/leads/${lead.id}`)}
                    className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm min-h-[44px]"
                  >
                    Dettagli
                  </button>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Empty State */}
        {(selectedStageFilter === 'all' 
          ? leads.filter(lead => {
              const searchLower = searchTerm.trim().toLowerCase();
              if (searchLower) {
                const contact = lead.contact_id ? contacts[lead.contact_id] : undefined;
                const titleMatch = lead.title?.toLowerCase().includes(searchLower) ?? false;
                const contactNameMatch = contact?.full_name?.toLowerCase().includes(searchLower) ?? false;
                if (!titleMatch && !contactNameMatch) return false;
              }
              if (selectedTags.length > 0) {
                const contact = lead.contact_id ? contacts[lead.contact_id] : undefined;
                if (!contact) return false;
                const firstTag = (contact.tags || [])[0];
                if (!firstTag) return false;
                const tagMatches = selectedTags.some(selectedTag => 
                  firstTag.toLowerCase() === selectedTag.toLowerCase()
                );
                if (!tagMatches) return false;
              }
              return true;
            }).length === 0
          : grouped[selectedStageFilter].length === 0
        ) && (
          <div className="p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-center bg-gray-50 dark:bg-gray-700/50">
            <p className="text-gray-500 dark:text-gray-400">Nessun lead</p>
          </div>
        )}
      </div>

      {showModal && canEdit && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{modalMode === 'new' ? t('crm.leads.modal.newTitle') : t('crm.leads.modal.editTitle')}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100">{t('crm.common.buttons.close')}</button>
            </div>
            <div className="grid gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">{t('crm.leads.modal.titleLabel')} *</label>
                <input
                  className="w-full bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="Es: Check-up per [Nome Azienda]"
                  value={leadForm.title}
                  onChange={(e) => setLeadForm((v) => ({ ...v, title: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">{t('crm.leads.modal.valueLabel')}</label>
                  <input
                    type="number"
                    className="w-full bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="0"
                    value={leadForm.value}
                    onChange={(e) => setLeadForm((v) => ({ ...v, value: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">{t('crm.leads.modal.stageLabel')}</label>
                  <select
                    className="w-full bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    value={leadForm.stage}
                    onChange={(e) => setLeadForm((v) => ({ ...v, stage: e.target.value as LeadStage }))}
                  >
                    {stages.map((stage) => (
                      <option key={stage} value={stage}>
                        {stageLabels[stage]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">{t('crm.leads.modal.notes')}</label>
                <textarea
                  className="w-full bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 min-h-[80px] resize-y"
                  placeholder={t('crm.leads.modal.notesPlaceholder')}
                  value={leadForm.notes}
                  onChange={(e) => setLeadForm((v) => ({ ...v, notes: e.target.value }))}
                />
              </div>
              {modalMode === 'new' ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('crm.leads.modal.linkContact')}</div>
                    <button
                      type="button"
                      onClick={() => {
                        setMultiSelectMode(!multiSelectMode);
                        if (!multiSelectMode) {
                          setSelectedContact(null);
                          setSelectedContacts([]);
                        } else {
                          setSelectedContacts([]);
                        }
                        setContactSearch('');
                      }}
                      className="text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-500 font-medium"
                    >
                      {multiSelectMode ? t('crm.leads.modal.singleMode') : t('crm.leads.modal.multiMode')}
                    </button>
                  </div>
                  <div className="relative">
                    <div className="flex items-center gap-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
                      <Search className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <input
                        className="bg-transparent flex-1 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                        placeholder={multiSelectMode ? t('crm.common.searchPlaceholder') : t('crm.common.searchPlaceholder')}
                        value={contactSearch}
                        onChange={(e) => {
                          setContactSearch(e.target.value);
                          if (selectedContact) setSelectedContact(null);
                          setShowContactList(true);
                        }}
                        disabled={Boolean(selectedContact) && !multiSelectMode}
                      />
                      {!multiSelectMode && selectedContact && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedContact(null);
                            setContactSearch('');
                            setLeadForm((v) => ({ ...v, contactName: '', contactEmail: '', contactPhone: '' }));
                            setContactWarning(null);
                          }}
                          className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                        >
                          {t('crm.leads.modal.remove')}
                        </button>
                      )}
                      {contactLoading && <span className="text-xs text-gray-500 dark:text-gray-400">{t('crm.leads.modal.loading')}</span>}
                      {multiSelectMode && selectedContacts.length > 0 && (
                        <span className="text-xs font-semibold text-cyan-600 dark:text-cyan-400">
                          {selectedContacts.length} {t('crm.leads.modal.selected')}
                        </span>
                      )}
                    </div>
                    {showContactList && contactOptions.length > 0 && (
                      <div className="absolute z-30 mt-2 w-full max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
                        {contactOptions.map((option) => {
                          const isSelected = multiSelectMode 
                            ? selectedContacts.some(c => c.id === option.id)
                            : selectedContact?.id === option.id;
                          
                          return (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => {
                                if (multiSelectMode) {
                                  if (isSelected) {
                                    setSelectedContacts(selectedContacts.filter(c => c.id !== option.id));
                                  } else {
                                    setSelectedContacts([...selectedContacts, option]);
                                  }
                                  setContactSearch('');
                                } else {
                                  setSelectedContact(option);
                                  setLeadForm((v) => ({
                                    ...v,
                                    contactName: option.full_name || '',
                                    contactEmail: option.email || '',
                                    contactPhone: option.phone || '',
                                  }));
                                  setContactSearch(option.full_name || option.email || '');
                                  setContactOptions([]);
                                  setShowContactList(false);
                                }
                              }}
                              className={`w-full text-left px-3 py-2.5 text-sm transition border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                                isSelected 
                                  ? 'bg-cyan-50 dark:bg-cyan-900/30 hover:bg-cyan-100 dark:hover:bg-cyan-900/40' 
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    {option.full_name || option.email || 'Senza nome'}
                                    {isSelected && <CheckCircle2 className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />}
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-300 flex gap-2 flex-wrap mt-0.5">
                                    {option.email && <span>{option.email}</span>}
                                    {option.phone && <span>{option.phone}</span>}
                                    {option.last_activity_at && <span>{formatRelative(option.last_activity_at)}</span>}
                                    {option.source && <span>{option.source}</span>}
                                  </div>
                                  {option.tags && option.tags.length > 0 && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {option.tags.slice(0, 3).map((tag) => (
                                        <span key={tag} className="inline-flex items-center gap-1 rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-1.5 py-0.5 text-[10px] text-gray-700 dark:text-gray-300">
                                          <Tags className="w-2.5 h-2.5" /> {tag}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                      {multiSelectMode && selectedContacts.length > 0 ? (
                        <div className="space-y-2">
                          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            {t('crm.leads.modal.attachedContact')} ({selectedContacts.length})
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedContacts.map((contact) => (
                              <div
                                key={contact.id}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 border border-cyan-200 dark:border-cyan-700 text-sm"
                              >
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                  {contact.full_name || contact.email || 'Senza nome'}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setSelectedContacts(selectedContacts.filter(c => c.id !== contact.id))}
                                  className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-500"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : selectedContact ? (
                    <div className="rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300 flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" /> {selectedContact.full_name || selectedContact.email || t('crm.leads.modal.attachedContact')}
                        </div>
                        <div className="text-xs text-emerald-600 dark:text-emerald-400">
                          {selectedContact.email || '—'} · {selectedContact.phone || '—'}
                        </div>
                        {contactWarning && (
                          <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> {contactWarning}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('crm.leads.modal.orCreateNew')}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                          placeholder="Nome contatto"
                          value={leadForm.contactName}
                          onChange={(e) => setLeadForm((v) => ({ ...v, contactName: e.target.value }))}
                        />
                        <input
                          className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                          placeholder="Telefono"
                          value={leadForm.contactPhone}
                          onChange={(e) => setLeadForm((v) => ({ ...v, contactPhone: e.target.value }))}
                        />
                      </div>
                      <input
                        className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                        placeholder="Email"
                        value={leadForm.contactEmail}
                        onChange={(e) => setLeadForm((v) => ({ ...v, contactEmail: e.target.value }))}
                      />
                    </>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('crm.leads.modal.contactDetailsNote')}</div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button 
                onClick={() => {
                  setShowModal(false);
                  setSelectedContacts([]);
                  setMultiSelectMode(false);
                }} 
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {t('crm.common.buttons.cancel')}
              </button>
              <button 
                onClick={handleSaveLead} 
                className="px-3 py-1.5 rounded bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors shadow-md"
                disabled={modalMode === 'new' && multiSelectMode && selectedContacts.length === 0 && !selectedContact && !leadForm.contactName}
              >
                {modalMode === 'new' 
                  ? (multiSelectMode && selectedContacts.length > 0 
                      ? `${t('crm.leads.modal.addLead')} (${selectedContacts.length})`
                      : t('crm.leads.modal.addLead'))
                  : t('crm.leads.modal.saveLead')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && canEdit && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('crm.leads.modal.deleteTitle')}</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('crm.leads.modal.deleteText', { title: confirmDelete.title || t('crm.leads.defaults.fallbackTitle') })}
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                {t('crm.common.buttons.cancel')}
              </button>
              <button onClick={() => handleDeleteLead(confirmDelete.id)} className="px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 transition-colors shadow-md">
                {t('crm.common.buttons.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;


